import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageParts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Plus, UserCog, Ban, Check } from "lucide-react";
import { useAuth } from "@/lib/auth";

const ROLE_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "account", label: "Account" },
  { value: "superadmin", label: "Superadmin" },
];

const ROLE_TONE = {
  superadmin: "bg-emerald-50 text-emerald-700 border-emerald-200",
  admin: "bg-blue-50 text-blue-700 border-blue-200",
  account: "bg-amber-50 text-amber-700 border-amber-200",
};

export default function UsersManagement() {
  const { user: current } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/users");
      setRows(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const changeRole = async (u, newRole) => {
    if (u.role === newRole) return;
    try {
      await api.patch(`/auth/users/${u.id}`, { role: newRole });
      toast.success(`${u.email} is now ${newRole}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to update role");
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/auth/users/${u.id}`, { active: !u.active });
      toast.success(`${u.email} ${!u.active ? "activated" : "deactivated"}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to update user");
    }
  };

  return (
    <>
      <PageHeader
        title="Users"
        subtitle="Manage all registered users and their assigned role. New users default to Admin."
      >
        <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="user-create-open">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> New user
        </Button>
      </PageHeader>
      <PageBody className="space-y-4">
        <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="w-40">Role</TableHead>
                <TableHead className="w-24">Active</TableHead>
                <TableHead className="w-40">Created</TableHead>
                <TableHead className="w-32 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">Loading…</TableCell></TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">No users found.</TableCell></TableRow>
              )}
              {!loading && rows.map((u) => {
                const isSelf = current?.id === u.id;
                const isSuperadmin = u.role === "superadmin";
                return (
                  <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                    <TableCell>
                      <div className="font-medium text-sm">{u.name}</div>
                      {isSelf && <div className="text-[10px] uppercase tracking-wider text-emerald-600 mt-0.5">You</div>}
                    </TableCell>
                    <TableCell className="text-xs font-mono">{u.email}</TableCell>
                    <TableCell>
                      <Select
                        value={u.role}
                        onValueChange={(v) => changeRole(u, v)}
                        disabled={isSelf && isSuperadmin}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid={`user-role-select-${u.email}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLE_OPTIONS.map((r) => (
                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Badge variant="outline" className={`mt-1 text-[10px] ${ROLE_TONE[u.role] || ""}`}>{u.role}</Badge>
                    </TableCell>
                    <TableCell>
                      {u.active ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><Check className="w-3 h-3" /> Yes</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-slate-500 text-xs"><Ban className="w-3 h-3" /> No</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500 font-mono">{fmtDate(u.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleActive(u)}
                        disabled={isSelf}
                        data-testid={`user-toggle-active-${u.email}`}
                      >
                        {u.active ? "Deactivate" : "Activate"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <p className="text-xs text-slate-500">
          <UserCog className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
          Only Superadmin can create users or change roles. You cannot demote or deactivate your own account.
        </p>
      </PageBody>

      <NewUserDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={() => { setCreateOpen(false); load(); }} />
    </>
  );
}

function NewUserDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "admin" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.email || !form.password || !form.name) {
      toast.error("Fill in name, email and password");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/users", { ...form, email: form.email.toLowerCase().trim() });
      toast.success(`User created as ${form.role}`);
      setForm({ email: "", password: "", name: "", role: "admin" });
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Create new user</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase">Full name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-create-name" />
          </div>
          <div>
            <Label className="text-xs uppercase">Email</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-create-email" />
          </div>
          <div>
            <Label className="text-xs uppercase">Temporary password</Label>
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-create-password" />
          </div>
          <div>
            <Label className="text-xs uppercase">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="user-create-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-500 mt-1">New users default to Admin unless changed.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="user-create-submit">{saving ? "Saving…" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
